import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskedLines, MaskedWords, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import FamilyDealer from "@/components/FamilyDealer";
import ProductShowcase from "@/components/ProductShowcase";
import ServiceAreasStrip from "@/components/ServiceAreasStrip";
import QuoteCTA from "@/components/QuoteCTA";
import PromoStrip from "@/components/PromoStrip";
import TrustBadges from "@/components/TrustBadges";

const HERO_IMG =
  "https://images.unsplash.com/photo-1548839140-29a749e1cf4d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70&auto=format";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen pt-56 pb-24 lg:pt-52 lg:pb-32 px-6 lg:px-10 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-10"
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            filter: "brightness(0.35) saturate(0.85)",
          }}
        />
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(ellipse at 70% 30%, rgba(46,126,232,0.28) 0%, transparent 55%), linear-gradient(180deg, rgba(4,15,15,0.35) 0%, rgba(4,15,15,0.85) 60%, var(--bg) 100%)",
        }} />
      </motion.div>

      <div className="ambient-glow-blue" style={{ top: "20%", left: "-10%" }} />

      <div className="mx-auto max-w-[1400px] relative">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4 mb-10"
        >
          <div className="h-[1px] w-16 bg-[color:var(--blue)]" />
          <div className="text-[11px] uppercase tracking-[0.4em] text-[color:var(--muted)]">
            Vanguard Water Systems · Perfecting Water · Protecting Life · Northern Utah &amp; Southern Idaho Kinetico Dealer
          </div>
        </motion.div>

        {/* Kinetic hero */}
        <MaskedLines
          testId="hero-headline"
          lines={[
            'Protect what',
            '<span class="font-editorial font-normal text-[color:var(--blue)] italic">matters.</span>',
            "Water perfected.",
          ]}
          className="font-display font-semibold text-white text-[clamp(3.2rem,10vw,10rem)] leading-[0.9]"
          stagger={0.14}
          delay={0.35}
        />

        <div className="mt-14 grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-5 lg:col-start-2">
            <div className="text-[15px] lg:text-[17px] leading-[1.7] text-[color:var(--muted)] font-body max-w-xl">
              <MaskedWords
                text="Vanguard Water Systems is perfecting water and protecting life across Northern Utah and Southern Idaho — Kinetico's non-electric, twin-tank technology, installed by the Vanguard Water Systems team."
                delay={1.2}
                stagger={0.015}
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex flex-col justify-end gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.6 }}
              className="flex flex-wrap gap-3"
            >
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
                Book a Free Water Test <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/products" className="btn-ghost" data-testid="hero-cta-products">
                Explore Systems
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Meta strip */}
        <motion.div
          style={{ opacity }}
          className="mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 border-t border-white/10 pt-10"
        >
          {[
            { k: "30+", v: "Years serving the region" },
            { k: "Twin-Tank", v: "Non-electric technology" },
            { k: "Free", v: "In-home water test" },
          ].map((m, i) => (
            <Reveal key={m.k} delay={0.05 * i} y={20}>
              <div className="font-display text-4xl lg:text-5xl font-semibold text-white tabular">{m.k}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.3em] text-[color:var(--muted)]">{m.v}</div>
            </Reveal>
          ))}
        </motion.div>

        <div className="mt-20 lg:mt-28 flex items-center gap-3 text-[color:var(--muted)] text-[11px] uppercase tracking-[0.35em]">
          <ChevronDown className="h-4 w-4 animate-bounce" /> Scroll
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <FamilyDealer />
      <Manifesto />
      <ProductShowcase />
      <PromoStrip />
      <ServiceAreasStrip />
      <TrustBadges />
      <QuoteCTA />
    </div>
  );
}
